import React from 'react';
import type { VisitsSeriesPoint } from '@troia-v3/types';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartLegendChip, DashCard, formatNumber, formatPct } from '@/shared/components/ui/dashboard';

interface Props {
  series: VisitsSeriesPoint[];
  granularity: 'day' | 'month';
}

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * Cor por TOKEN, não pela paleta monocromática de `CHART_COLORS`.
 *
 * `CHART_COLORS` é uma escala de roxo pensada para fatias da MESMA grandeza
 * (um gráfico de origens, por exemplo). Visitas, leads e vendas são coisas
 * diferentes, e tonalidades do mesmo roxo não separam as séries. É a convenção
 * que o "Volume por dia" do dashboard de Atendimento já usa — e, sendo token,
 * acompanha o tema do cliente e o modo escuro.
 */
const VIEWS_COLOR = 'var(--theme-primary)';
const LEADS_COLOR = 'var(--theme-info-color)';
const SALES_COLOR = 'var(--theme-success-color)';

const formatLabel = (date: string, granularity: 'day' | 'month'): string => {
  const [y, m, d] = date.split('-');
  if (granularity === 'month') return `${MONTHS[Number(m) - 1] ?? ''}/${String(y).slice(-2)}`;
  return `${d}/${m}`;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * Chave do período que ainda está acontecendo, no formato do backend
 * (`bucketFormat`: `%Y-%m-%d` por dia, `%Y-%m-01` por mês).
 *
 * Desde 19/10/2026 a Best Flow pode mandar o movimento de HOJE de hora em hora,
 * então a barra de hoje deixa de ser "sem medição" e passa a ter um número —
 * menor que o dos outros dias, porque o dia não acabou. Sem dizer isso, a barra
 * curta seria lida como queda de movimento.
 */
const currentBucketKey = (granularity: 'day' | 'month'): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  if (granularity === 'month') return `${y}-${m}-01`;
  return `${y}-${m}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Tooltip sempre em tema claro (padrão do projeto para tooltip de gráfico).
 *
 * Duas distinções que ele precisa fazer:
 *
 *  - "0 visitas" (a loja reportou e ninguém entrou) versus "sem medição"
 *    (ninguém reportou). O contador de fluxo OMITE a janela sem movimento, e
 *    tratar os dois como zero transformaria falha de coleta em queda de movimento.
 *  - Lead e venda **independem** de medição. Um dia sem sensor reportando pode
 *    ter tido lead e venda, e escondê-los atrás do "sem medição" apagaria um
 *    fato conhecido.
 */
const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: VisitsSeriesPoint }>;
  granularity: 'day' | 'month';
}> = ({ active, payload, granularity }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const inProgress = point.date === currentBucketKey(granularity);

  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', color: '#1e293b' }}
    >
      <div className="font-semibold mb-1">{formatLabel(point.date, granularity)}</div>
      {inProgress && (
        <div className="mb-1" style={{ color: '#64748b' }} data-testid="analytics-visits-series-in-progress">
          {granularity === 'month' ? 'Mês em andamento' : 'Dia em andamento'} — números parciais
        </div>
      )}

      {point.measured ? (
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm flex-none" style={{ backgroundColor: VIEWS_COLOR }} />
          <span>{formatNumber(point.views)} visitas</span>
          <span style={{ color: '#64748b' }}>
            · {point.unitsReporting} {point.unitsReporting === 1 ? 'unidade' : 'unidades'}
          </span>
        </div>
      ) : (
        <div style={{ color: '#b45309' }}>Sem medição de visitas</div>
      )}

      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="w-2 h-2 rounded-sm flex-none" style={{ backgroundColor: LEADS_COLOR }} />
        <span>
          {formatNumber(point.leads ?? 0)} {point.leads === 1 ? 'lead' : 'leads'}
        </span>
      </div>

      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="w-2 h-2 rounded-sm flex-none" style={{ backgroundColor: SALES_COLOR }} />
        <span>
          {formatNumber(point.sales ?? 0)} {point.sales === 1 ? 'venda' : 'vendas'}
        </span>
        {(point.revenue ?? 0) > 0 && (
          <span style={{ color: '#64748b' }}>· {formatCurrency(point.revenue ?? 0)}</span>
        )}
      </div>
    </div>
  );
};

/**
 * Visitas, leads e vendas por período, lado a lado — o funil na ordem.
 *
 * ⚠️ **Dois eixos, e isso não é enfeite.** No tenant de referência o mês tem
 * 3.081 visitas com pico de 792 num dia, contra 0-2 vendas. No mesmo eixo, uma
 * venda ocuparia `2/792 × 260px ≈ 0,7 pixel` — some. Empilhar (como o "Volume
 * por dia" faz) funciona lá porque as séries têm tamanhos parecidos e somam
 * um total com significado; aqui não somam nada e uma delas desaparece. Por
 * isso leads e vendas leem o eixo da DIREITA, com escala própria. Os dois
 * dividem esse eixo porque são a mesma unidade — venda é lead ganho.
 */
export const VisitsSeriesCard: React.FC<Props> = ({ series, granularity }) => {
  const maxViews = Math.max(1, ...series.map((p) => p.views));
  const dense = series.length > 31;
  const gaps = series.filter((p) => !p.measured).length;
  const totalViews = series.reduce((sum, p) => sum + p.views, 0);
  // `?? 0` não é paranoia: o payload é servido de um cache de 5 minutos que NÃO
  // versiona o formato. No deploy, as entradas gravadas antes ainda vêm sem
  // `sales`, e `formatNumber(undefined)` desenha "NaN" na legenda por até 5
  // minutos — no lugar mais visível do card.
  const totalSales = series.reduce((sum, p) => sum + (p.sales ?? 0), 0);
  const totalLeads = series.reduce((sum, p) => sum + (p.leads ?? 0), 0);
  // Sem visita medida não há denominador: "0%" diria que ninguém virou lead.
  const conversion = totalViews > 0 ? formatPct((totalLeads / totalViews) * 100) : null;

  // Barra mínima visível para o bucket sem medição: some do gráfico se ficar em
  // zero, e sumir é justamente o que confunde ausência com queda. Vale só para
  // visitas — venda zero é zero de verdade, não ausência de dado.
  const data = series.map((p) => ({
    ...p,
    sales: p.sales ?? 0,
    revenue: p.revenue ?? 0,
    leads: p.leads ?? 0,
    plotted: p.measured ? p.views : maxViews * 0.02,
  }));

  return (
    <DashCard
      icon="bar-chart"
      title="Visitas por período"
      {...(gaps > 0
        ? { subtitle: `${gaps} ${gaps === 1 ? 'período sem medição' : 'períodos sem medição'}` }
        : {})}
      testId="analytics-visits-series-card"
    >
      {/* Legenda no corpo, não no `headerRight`: o cabeçalho não encolhe, e três
          chips (um deles com a conversão) empurravam o título para fora no
          celular. Aqui ela quebra linha. */}
      <div
        className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 mb-2"
        data-testid="analytics-visits-series-legend"
      >
        <ChartLegendChip
          color={VIEWS_COLOR}
          label="Visitas"
          total={totalViews}
          testId="analytics-visits-series-legend-views"
        />
        <ChartLegendChip
          color={LEADS_COLOR}
          label="Leads"
          total={totalLeads}
          {...(conversion ? { extra: `${conversion} das visitas` } : {})}
          testId="analytics-visits-series-legend-leads"
        />
        <ChartLegendChip
          color={SALES_COLOR}
          label="Vendas"
          total={totalSales}
          testId="analytics-visits-series-legend-sales"
        />
      </div>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: -8, left: -12, bottom: dense ? 20 : 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--theme-card-border)" />
            <XAxis
              dataKey="date"
              tickFormatter={(v: string) => formatLabel(v, granularity)}
              tick={{ fontSize: dense ? 9 : 11, fill: 'var(--theme-muted-text)' }}
              interval={dense ? Math.floor(series.length / 15) : 0}
              angle={dense ? -45 : 0}
              textAnchor={dense ? 'end' : 'middle'}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="views"
              tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            {/* Eixo próprio de leads e vendas. `domain` com piso 1 evita um eixo
                0..0 ilegível no período sem nenhum lead, e `allowDecimals={false}`
                impede "0,5 venda" na régua. */}
            <YAxis
              yAxisId="leads"
              orientation="right"
              domain={[0, (max: number) => Math.max(1, max)]}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<CustomTooltip granularity={granularity} />} cursor={{ fill: 'transparent' }} />

            <Bar yAxisId="views" dataKey="plotted" name="Visitas" radius={[4, 4, 0, 0]}>
              {data.map((p) => (
                <Cell
                  key={p.date}
                  fill={p.measured ? VIEWS_COLOR : 'var(--theme-surface-sunken)'}
                  {...(p.measured ? {} : { 'data-testid': `analytics-visits-series-gap-${p.date}` })}
                />
              ))}
            </Bar>

            <Bar
              yAxisId="leads"
              dataKey="leads"
              name="Leads"
              fill={LEADS_COLOR}
              radius={[4, 4, 0, 0]}
            />

            <Bar
              yAxisId="leads"
              dataKey="sales"
              name="Vendas"
              fill={SALES_COLOR}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashCard>
  );
};

VisitsSeriesCard.displayName = 'VisitsSeriesCard';
